import React, { useContext, useEffect, useState } from 'react'
import {
    Alert,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Nav,
    NavItem,
    NavLink,
    Row,
    Table
} from 'reactstrap'
import { useLocation } from 'react-router-dom'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'
import ApiService from '../../data/services/ApiService'
import { GeneralContext } from '../../context'
import { saveStream } from '../../helpers/utils'
import PricingShippingImport from './components/PricingShippingImport'
import PricingShippingMaterials from './components/PricingShippingMaterials'

const tabs = {
    catalog: {
        label: 'Catálogo',
        title: 'Actualización masiva de catálogo',
        description: 'Actualiza productos y variantes existentes. Las categorías, subcategorías y marcas se editan por nombre; las imágenes no se mueven de almacenamiento.',
        template: () => ApiService.exportCatalogBulkTemplate(),
        validate: data => ApiService.validateCatalogBulk(data),
        apply: data => ApiService.importCatalogBulk(data)
    },
    pricing: {
        label: 'Precios y envíos',
        title: 'Matriz de precios y envíos',
        description: 'Valida y revisa los cambios de precios públicos y perfiles de envío antes de confirmar su aplicación.',
        template: (scope, codes) => ApiService.exportPricingShippingBulkTemplate(scope, codes)
    },
    costs: {
        label: 'Costos operativos',
        title: 'Costos unitarios operativos',
        description: 'Actualiza ProductVariant.unitCost solo para reportes operativos. Esto nunca modifica los precios públicos.',
        template: () => ApiService.exportOperationalCostsBulkTemplate(),
        validate: null,
        apply: data => ApiService.importOperationalCostsBulk(data)
    }
}

const buildFormData = (file) => {
    const data = new FormData()
    data.append('file', file)
    data.append('identifier', (Math.random() + 1).toString(36).substring(2, 8).toUpperCase())
    return data
}

const issuesToCsv = issues => {
    const rows = [['rowNumber', 'field', 'message']]
    ;(issues || []).forEach(issue => rows.push([issue.rowNumber || '', issue.field || '', issue.message || '']))
    return rows.map(row => row.map(value => {
        const text = `${value}`
        return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
    }).join(',')).join('\n')
}

const getBulkResult = response => response?.data?.result || response?.data?.process?.data || null

const BulkResult = ({ result }) => {
    if (!result) return null
    const totals = result.totals || {}
    const errors = result.errors || []
    const warnings = result.warnings || []

    return (
        <Card className="mt-3">
            <CardBody>
                <h5>Resultado</h5>
                <Row>
                    <Col md={2}><strong>Filas</strong><br />{totals.rows || 0}</Col>
                    <Col md={2}><strong>Actualizadas</strong><br />{totals.updated || 0}</Col>
                    <Col md={2}><strong>Omitidas</strong><br />{totals.skipped || 0}</Col>
                    <Col md={2}><strong>Errores</strong><br />{totals.errors || 0}</Col>
                    <Col md={2}><strong>Alertas</strong><br />{totals.warnings || 0}</Col>
                </Row>

                {errors.length > 0 && (
                    <React.Fragment>
                        <Alert color="danger" className="mt-3">Corrige los errores antes de aplicar el archivo.</Alert>
                        <Button size="sm" color="secondary" onClick={() => saveStream('text/csv', 'bulk-errors.csv', issuesToCsv(errors))}>Descargar errores</Button>
                    </React.Fragment>
                )}

                {warnings.length > 0 && <Alert color="warning" className="mt-3">Hay alertas no bloqueantes en el archivo.</Alert>}

                {(errors.length > 0 || warnings.length > 0) && (
                    <Table size="sm" responsive className="mt-3">
                        <thead><tr><th>Fila</th><th>Campo</th><th>Mensaje</th></tr></thead>
                        <tbody>
                            {[...errors, ...warnings].slice(0, 25).map((issue, index) => (
                                <tr key={`${issue.rowNumber}-${issue.field}-${index}`}>
                                    <td>{issue.rowNumber}</td>
                                    <td>{issue.field}</td>
                                    <td>{issue.message}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </CardBody>
        </Card>
    )
}

const BulkOperations = () => {
    const { setLoading, setBreadcrumb } = useContext(GeneralContext)
    const { search } = useLocation()
    const params = new URLSearchParams(search)
    const requestedTab = params.get('tab')
    const selectedMaterial = params.get('material')
    const [activeTab, setActiveTab] = useState(Object.prototype.hasOwnProperty.call(tabs, requestedTab) ? requestedTab : 'catalog')
    const [exportScope, setExportScope] = useState(selectedMaterial ? 'complete' : 'storefront')
    const [materialCodes, setMaterialCodes] = useState(selectedMaterial ? [selectedMaterial] : null)
    const [materialsAvailable, setMaterialsAvailable] = useState(false)
    const [file, setFile] = useState(null)
    const [result, setResult] = useState(null)
    const [validated, setValidated] = useState(false)
    const [pricingBusy, setPricingBusy] = useState(false)

    useEffect(() => {
        setBreadcrumb({
            icon: '',
            parent: 'Productos',
            parentLink: '/products',
            item: 'Actualización masiva'
        })
    }, []) // eslint-disable-line

    const current = tabs[activeTab]

    const resetState = nextTab => {
        setActiveTab(nextTab)
        setFile(null)
        setResult(null)
        setValidated(false)
    }

    useEffect(() => {
        resetState(Object.prototype.hasOwnProperty.call(tabs, requestedTab) ? requestedTab : 'catalog')
        setExportScope(selectedMaterial ? 'complete' : 'storefront')
    }, [search]) // eslint-disable-line

    useEffect(() => {
        if (selectedMaterial) setMaterialCodes([selectedMaterial])
    }, [selectedMaterial])

    const materialsLoaded = materials => {
        setMaterialsAvailable(materials.length > 0)
        setMaterialCodes(current => current === null ? materials.map(item => item.code) : current)
    }

    const handleFile = event => {
        const selected = event.target.files[0]
        if (!selected) return
        const validTypes = ['text/csv', 'application/vnd.ms-excel', 'text/comma-separated-values', 'application/csv', 'application/excel', 'application/vnd.msexcel']
        if (selected.type && validTypes.every(type => selected.type !== type)) {
            toastr.error('Solo se aceptan archivos CSV', 'Error')
            return
        }
        setFile(selected)
        setResult(null)
        setValidated(false)
    }

    const downloadTemplate = async () => {
        if (activeTab === 'pricing' && (!materialsAvailable || !materialCodes?.length)) return
        try {
            setLoading(true)
            await current.template(exportScope, materialCodes)
        } catch (error) {
            toastr.error(error.response?.data?.message || 'No pude descargar la plantilla')
        } finally {
            setLoading(false)
        }
    }

    const validateFile = async () => {
        if (!file) return toastr.error('Selecciona un archivo CSV')
        if (!current.validate) {
            setValidated(true)
            setResult(null)
            return toastr.info('Este archivo se validará al aplicar la importación')
        }
        try {
            setLoading(true)
            const response = await current.validate(buildFormData(file))
            const bulkResult = getBulkResult(response)
            if (!bulkResult) {
                setResult(null)
                setValidated(false)
                return toastr.error('El servidor no devolvió el resultado de validación')
            }
            setResult(bulkResult)
            setValidated(response.data.success && (bulkResult.totals?.errors || 0) === 0)
            if (response.data.success) toastr.success('Archivo validado')
            else toastr.error('El archivo tiene errores')
        } catch (error) {
            toastr.error(error.response?.data?.message || 'No pude validar el archivo')
        } finally {
            setLoading(false)
        }
    }

    const applyImport = async () => {
        if (!file) return toastr.error('Selecciona un archivo CSV')
        if (current.validate && !validated) return toastr.error('Valida el archivo antes de aplicar cambios')
        try {
            setLoading(true)
            const response = await current.apply(buildFormData(file))
            const bulkResult = getBulkResult(response)
            setResult(bulkResult)
            setValidated(response.data.success && (bulkResult?.totals?.errors || 0) === 0)
            if (response.data.success) toastr.success('Actualización aplicada')
            else toastr.error('No se aplicaron cambios por errores en el archivo')
        } catch (error) {
            const responseResult = getBulkResult(error.response)
            if (responseResult) setResult(responseResult)
            toastr.error(error.response?.data?.message || 'No pude aplicar la importación')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Card>
                    <CardBody>
                        <Nav tabs>
                            {Object.keys(tabs).map(key => (
                                <NavItem key={key}>
                                    <NavLink href="#" active={activeTab === key} disabled={pricingBusy} onClick={event => { event.preventDefault(); if (!pricingBusy) resetState(key) }}>
                                        {tabs[key].label}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>

                        {activeTab === 'pricing' && <Alert color="info" className="mt-3">
                            <p>Solo se exportarán los materiales seleccionados. Las configuraciones omitidas no se modificarán al importar.</p>
                            <p className="mb-0">Antes de importar, revisa todas las filas del CSV: los cambios pueden afectar a otros materiales incluidos. Filtrar la vista en Excel no elimina esas filas del archivo.</p>
                        </Alert>}

                        <Row className="mt-4">
                            <Col md={5}>
                                <h4>{current.title}</h4>
                                <p className="text-muted">{current.description}</p>
                                {activeTab === 'pricing' && <React.Fragment>
                                    <h5>Exportar configuración</h5>
                                    <PricingShippingMaterials codes={materialCodes} onChange={setMaterialCodes} onAvailable={materialsLoaded} selectedMaterial={selectedMaterial} />
                                    <FormGroup>
                                    <Label for="pricing-export-scope">Alcance de exportación</Label>
                                    <Input id="pricing-export-scope" type="select" value={exportScope} onChange={event => setExportScope(event.target.value)}>
                                        <option value="storefront">Tienda</option>
                                        <option value="complete">Completo (incluye inactivos e históricos)</option>
                                    </Input>
                                    <small className="text-muted">Usa Completo para localizar y reactivar configuraciones inactivas. Las filas históricas pueden requerir correcciones antes de importar.</small>
                                </FormGroup></React.Fragment>}
                                <Button color="secondary" disabled={activeTab === 'pricing' && (!materialsAvailable || !materialCodes?.length)} onClick={downloadTemplate}>
                                    <i className="mdi mdi-download mr-1"></i> Descargar plantilla
                                </Button>
                            </Col>
                            <Col md={7}>
                                {activeTab === 'pricing' ? <PricingShippingImport key={search} selectedMaterial={selectedMaterial} onBusyChange={setPricingBusy} /> : <React.Fragment>
                                <FormGroup>
                                    <Label>Archivo CSV</Label>
                                    <Input type="file" accept=".csv,text/csv" onChange={handleFile} />
                                </FormGroup>
                                <Button color="info" disabled={!file} onClick={validateFile} className="mr-2">
                                    <i className="mdi mdi-check mr-1"></i> Validar
                                </Button>
                                <Button color="primary" disabled={!file || (current.validate && !validated)} onClick={applyImport}>
                                    <i className="mdi mdi-upload mr-1"></i> Aplicar cambios
                                </Button>
                                </React.Fragment>}
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {activeTab !== 'pricing' && <BulkResult result={result} />}
            </Container>
        </div>
    )
}

export default BulkOperations
